import { useContext, useState, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';

import { FilterField, FormPublishingFooter, Message } from '@components';
import { GlobalContext } from '@contexts';
import { DatamodelFormField, FormError } from '@Forms/fields.jsx';
import { errorsFromMutate } from '@Forms/utils.js';
import { matchDatamodelField } from '@helpers';
import { useRelatedMinMaxValidation } from '@hooks';
import get from 'lodash/get';

export const ManageContentForm = ({
  datamodelData,
  disabled,
  mutation,
  publishMutation,
  relatedDatamodels,
  siteData,
}) => {
  const [justReverted, setJustReverted] = useState(false);
  const useFormReturn = useForm({
    values: siteData,
    mode: 'onBlur',
  });
  const {
    handleSubmit,
    setError,
    control,
    watch,
    formState: { errors },
    clearErrors,
  } = useFormReturn;
  const { isDirty, isValid } = useFormState({ control });
  const globals = useContext(GlobalContext);

  const onSubmitError = (error) => {
    // handle server-side validations and errors
    const errorsList = errorsFromMutate(error);
    // Only set field errors, skip __all__ errors as they are handled in the parent component
    errorsList
      .filter(({ field }) => field !== '__all__')
      .forEach(({ field, error }) => setError(field, error));
  };

  const onSubmit = (data, e) => {
    // payload is just custom fields to avoid conflicting saves
    const payload = {
      custom_fields: data.custom_fields,
    };
    // determine draft vs publish based on which button was clicked.
    // publishing only applies the draft_action when a draft actually
    // exists to publish, otherwise this is a normal immediate save.
    const submitter = e?.nativeEvent?.submitter;
    if (submitter?.name === 'publish') {
      if (siteData.has_draft) {
        payload.draft_action = 'publish';
      }
      mutation.reset();
      setJustReverted(false);
      publishMutation.mutate(payload, { onError: onSubmitError });
    } else {
      payload.draft_action = 'save';
      publishMutation.reset();
      setJustReverted(false);
      mutation.mutate(payload, { onError: onSubmitError });
    }
  };

  const onRevert = () => {
    publishMutation.reset();
    setJustReverted(true);
    mutation.mutate({ draft_action: 'delete' }, { onError: onSubmitError });
  };

  // filter search term
  const [searchTerm, setSearchTerm] = useState('');

  const relatedModelListFields = useMemo(() => {
    return (datamodelData?.fields || [])
      .filter((f) => f.field_type === 'related_model_list')
      .map((f) => ({
        slug: f.slug,
        required: !!f.required,
        min_items: f.min_items,
        max_items: f.max_items,
      }));
  }, [datamodelData?.fields]);

  // validate min_items and max_items
  useRelatedMinMaxValidation({
    fields: relatedModelListFields,
    namePrefix: 'custom_fields',
    watch,
    errors,
    setError,
    clearErrors,
  });

  // list of dynamic fields
  // prefix added to target custom_fields.
  let matchCount = 0;
  const fieldsList = datamodelData.fields.map((field) => {
    const match = matchDatamodelField(field, searchTerm);
    const fieldError = get(errors, `custom_fields.${field.slug}`);
    if (match) {
      matchCount++;
    }
    return (
      <div
        className={
          `sb-form-field-hideable ` + (match == false ? 'sb-hide' : '')
        }
      >
        {field.field_type === 'related_model_list' && fieldError?.message && (
          <FormError message={fieldError.message} />
        )}
        <DatamodelFormField
          key={field.id}
          datamodelField={field}
          fieldPrefix={`custom_fields.`}
          relatedDatamodels={relatedDatamodels}
          filterSearchTerm={searchTerm}
          siteID={siteData.id}
          useFormReturn={useFormReturn}
        />
      </div>
    );
  });

  return (
    <>
      <div className="sb-flex">
        <FilterField searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={
          `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
        }
      >
        <fieldset className="sb-form-item">
          <div className="sb-form-item-inner">
            <div className="sb-form-item-elements">{fieldsList}</div>
          </div>
        </fieldset>
        {searchTerm && (
          <Message
            messageStyle="notice"
            title={
              `Filtered to ${matchCount} of ${datamodelData.fields.length} field` +
              (datamodelData.fields.length != 1 ? 's' : '')
            }
            fadeoutMessage={false}
            isFormMessage={false}
          />
        )}
        <FormPublishingFooter
          disabled={disabled}
          draftMutation={mutation}
          draftUrl={`${siteData.preview_url}?cache=none`}
          isDirty={isDirty}
          isDraft={siteData.has_draft}
          isError={mutation.isError || publishMutation.isError}
          isValid={isValid}
          isReverted={justReverted}
          onRevert={onRevert}
          publishedUrl={
            siteData.published
              ? `${siteData.formatted_url}?cache=none`
              : siteData.preview_url
          }
          publishMutation={publishMutation}
          showDraft={!!globals.whitelabel.settings.site_drafts}
          showRevert={true}
        />
      </form>
    </>
  );
};
